import { useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { lenisStore } from "../lib/lenisStore";

const EASE = [0.22, 1, 0.36, 1];

export default function Lightbox({ images, index, onClose, onNavigate }) {
  const current = images[index];

  const prev = useCallback(
    () => onNavigate((index - 1 + images.length) % images.length),
    [index, images.length, onNavigate]
  );
  const next = useCallback(
    () => onNavigate((index + 1) % images.length),
    [index, images.length, onNavigate]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    lenisStore.instance?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      lenisStore.instance?.start();
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  return (
    <motion.div
      data-testid="lightbox-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 backdrop-blur-2xl"
      onClick={onClose}
    >
      <span className="absolute top-6 left-6 text-[11px] font-mono tracking-[0.25em] text-stone-500">
        {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
      </span>
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close lightbox"
        className="absolute top-5 right-5 z-20 p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
      >
        <X size={18} />
      </button>

      <div
        className="relative flex-1 flex items-center justify-center px-4 md:px-24 pt-14"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="lightbox-prev"
          onClick={prev}
          aria-label="Previous image"
          className="absolute left-4 md:left-10 z-20 p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
        >
          <ChevronLeft size={18} />
        </button>

        <motion.figure
          key={current.id}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: EASE }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.4}
          onDragEnd={(e, info) => {
            if (info.offset.x < -60) next();
            else if (info.offset.x > 60) prev();
          }}
          className="w-full max-w-4xl"
        >
          <img
            data-testid="lightbox-image"
            src={current.url}
            alt={current.title}
            draggable={false}
            className="mx-auto max-h-[58vh] w-auto max-w-full object-contain"
          />
          <figcaption
            data-testid="lightbox-caption"
            className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3"
          >
            <div>
              <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-champagne">
                {current.category} · {current.location}
              </p>
              <h3 className="mt-1 font-serif text-xl text-ivory">{current.title}</h3>
            </div>
            <p className="max-w-sm text-xs font-light text-stone-400 leading-relaxed">
              {current.story}
            </p>
          </figcaption>
        </motion.figure>

        <button
          data-testid="lightbox-next"
          onClick={next}
          aria-label="Next image"
          className="absolute right-4 md:right-10 z-20 p-3 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne transition-colors duration-300"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div
        className="relative z-10 flex justify-start md:justify-center gap-2 overflow-x-auto px-6 pb-6 pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, i) => (
          <button
            key={img.id}
            data-testid={`lightbox-thumb-${img.id}`}
            onClick={() => onNavigate(i)}
            aria-label={`Jump to ${img.title}`}
            className={`h-14 w-20 shrink-0 overflow-hidden border transition-all duration-300 ${
              i === index
                ? "border-gold opacity-100"
                : "border-transparent opacity-40 hover:opacity-80"
            }`}
          >
            <img src={img.url} alt={img.title} loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </motion.div>
  );
}
